import { Message, MessageRole } from '../types/chat';

const CHAT_EVENT_ROLES = new Set<MessageRole>([
  MessageRole.CONVERSATION_CREATED,
  MessageRole.ADD_PARTICIPANT,
  MessageRole.CONVERSATION_TITLE_UPDATED,
  MessageRole.USER_REMOVED,
]);

/** Title events that only show a day/time label (paired with add-participant at the same instant). */
const TIME_ONLY_TITLE_PATTERN =
  /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+\d{1,2}:\d{2}\s*(AM|PM)?$/i;

function getStableId(message: Message): string {
  return message.recordId || message.generations[0]?.id || '';
}

function getGenerationContent(message: Message): string {
  const idx = message.activeGenerationIndex ?? 0;
  return (message.generations[idx]?.content ?? message.generations[0]?.content ?? '').trim();
}

function getCreatedDate(message: Message): number | undefined {
  const idx = message.activeGenerationIndex ?? 0;
  return message.generations[idx]?.createdDate ?? message.generations[0]?.createdDate;
}

function preferIncomingMessage(existing: Message, incoming: Message): boolean {
  if (incoming.generations.length > existing.generations.length) return true;
  if (incoming.generations.some((g) => g.isThisGenerationOnGoing)) return true;
  const incomingContent = incoming.generations[incoming.generations.length - 1]?.content?.trim();
  const existingContent = existing.generations[existing.generations.length - 1]?.content?.trim();
  return Boolean(incomingContent) && !existingContent;
}

/**
 * Removes duplicate group-chat rows before render:
 * - same recordId / generation id
 * - only the first conversation-created event (later copies in the thread are dropped)
 * - repeated system events (same role, content, timestamp)
 * - time-only title-updated events that mirror add-participant at the same instant
 */
export function deduplicateGroupChatMessages(messages: Message[]): Message[] {
  if (messages.length === 0) return messages;

  const participantEventTimestamps = new Set<number>();
  for (const msg of messages) {
    const role = msg.role as MessageRole;
    if (role === MessageRole.ADD_PARTICIPANT || role === MessageRole.CONVERSATION_CREATED) {
      const ts = getCreatedDate(msg);
      if (ts != null) participantEventTimestamps.add(ts);
    }
  }

  const recordIdToIndex = new Map<string, number>();
  const seenSystemEventKeys = new Set<string>();
  let seenConversationCreated = false;
  const result: Message[] = [];

  for (const message of messages) {
    const role = message.role as MessageRole;

    // Only show the first "conversation-created" chip (duplicates often re-insert mid-thread).
    if (role === MessageRole.CONVERSATION_CREATED) {
      if (seenConversationCreated) continue;
      seenConversationCreated = true;
    }
    result.push(message);
  }

  return result;
}
